import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Sliders, Shield, Scale, Lock, Mic } from 'lucide-react';
import api from '../../api';
import VoiceSettings from '../../components/VoiceSettings';
import { PhotoUploader } from '../../components/PhotoAvatar';

export default function SettingsPage() {
  const { presenceId } = useParams();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('identity');
  const [presence, setPresence] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPresence();
  }, [presenceId]);

  const fetchPresence = async () => {
    try {
      const res = await api.get(`/presence/${presenceId}`);
      setPresence(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'identity', label: 'Identity', icon: User },
    { id: 'persona', label: 'Persona Style', icon: Sliders },
    { id: 'voice', label: 'Voice & Audio', icon: Mic },
    { id: 'values', label: 'Values & Boundaries', icon: Scale },
    { id: 'safety', label: 'Safety & Governance', icon: Shield },
    { id: 'access', label: 'Access Control', icon: Lock },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-400">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col lg:flex-row">
      {/* Mobile Tabs - Horizontal Scroll */}
      <div className="lg:hidden border-b border-slate-800 bg-slate-950/50 px-4 py-3 overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={[
                'flex items-center gap-2 px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-all',
                activeTab === tab.id
                  ? 'bg-cyan-400/10 text-cyan-50 border border-cyan-400'
                  : 'text-slate-400 hover:text-cyan-100 hover:bg-slate-900/50 border border-transparent'
              ].join(' ')}
            >
              <tab.icon size={14} className={activeTab === tab.id ? 'text-cyan-400' : 'text-slate-500'} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Desktop Sidebar - Tabs */}
      <aside className="hidden lg:block w-56 border-r border-slate-800 bg-slate-950/50 p-4">
        <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400/80 mb-3">
          ⚙️ Settings
        </div>
        <nav className="space-y-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={[
                'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all',
                activeTab === tab.id
                  ? 'bg-cyan-400/10 text-cyan-50 border-l-2 border-cyan-400'
                  : 'text-slate-400 hover:text-cyan-100 hover:bg-slate-900/50 border-l-2 border-transparent'
              ].join(' ')}
            >
              <tab.icon size={16} className={activeTab === tab.id ? 'text-cyan-400' : 'text-slate-500'} />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto p-4 lg:p-6">
        {activeTab === 'identity' && <IdentityTab presence={presence} onUpdate={fetchPresence} />}
        {activeTab === 'persona' && <PersonaTab presence={presence} onUpdate={fetchPresence} />}
        {activeTab === 'voice' && <VoiceSettings presenceId={presenceId} />}
        {activeTab === 'values' && <ValuesTab presence={presence} onUpdate={fetchPresence} />}
        {activeTab === 'safety' && <SafetyTab presence={presence} onUpdate={fetchPresence} />}
        {activeTab === 'access' && <AccessTab presence={presence} onUpdate={fetchPresence} />}
      </main>
    </div>
  );
}

// Tab 1: Identity
function IdentityTab({ presence, onUpdate }) {
  const [formData, setFormData] = useState({
    name: presence?.name || '',
    description: presence?.description || '',
    status: presence?.status || 'active',
    photo_url: presence?.photo_url || null
  });
  const [saving, setSaving] = useState(false);

  const handlePhotoUpload = async (dataUrl, file) => {
    // Save photo URL to presence
    setFormData({ ...formData, photo_url: dataUrl });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/presence/${presence.id}`, formData);
      onUpdate();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <header className="mb-4 lg:mb-6">
        <h2 className="text-lg lg:text-xl font-bold mb-1">Presence Identity</h2>
        <p className="text-xs lg:text-sm text-slate-400">Basic information about this presence</p>
      </header>

      <div className="space-y-4 lg:space-y-6">
        {/* Avatar / Photo */}
        <div>
          <label className="block text-xs lg:text-sm font-medium text-slate-300 mb-2">
            Avatar Fotoğrafı
          </label>
          <p className="text-xs text-slate-400 mb-3">
            Voice conversation sırasında bu fotoğrafınız görünecek. 3D model yerine gerçek fotoğrafınızı kullanabilirsiniz.
          </p>
          <PhotoUploader
            onPhotoUpload={handlePhotoUpload}
            currentPhoto={formData.photo_url}
          />
        </div>

        {/* Name */}
        <div>
          <label className="block text-xs lg:text-sm font-medium text-slate-300 mb-2">Display Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 text-sm bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition"
            placeholder="My Digital Presence"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs lg:text-sm font-medium text-slate-300 mb-2">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 text-sm bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition resize-none"
            placeholder="A brief description of this presence..."
          />
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
          <div className="flex gap-3">
            {['active', 'paused'].map((status) => (
              <label key={status} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value={status}
                  checked={formData.status === status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="text-cyan-400"
                />
                <span className="text-sm text-slate-300 capitalize">{status}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Metadata */}
        <div className="pt-4 border-t border-slate-800">
          <div className="text-xs text-slate-500 space-y-1">
            <p>Created: {new Date(presence?.createdAt).toLocaleDateString()}</p>
            <p>Last Active: {presence?.lastActive ? new Date(presence.lastActive).toLocaleString() : 'Never'}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-cyan-400 text-slate-900 font-medium rounded-lg hover:bg-cyan-300 transition disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button className="px-4 py-2 text-sm border border-slate-700 text-slate-300 rounded-lg hover:bg-slate-800 transition">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Tab 2: Persona Style
function PersonaTab({ presence }) {
  const [sliders, setSliders] = useState({
    formality: 50,
    emotionalRange: 50,
    directness: 50,
    creativity: 50,
    humor: 50,
    technicalDepth: 50
  });

  const handleSliderChange = (key, value) => {
    setSliders({ ...sliders, [key]: value });
  };

  return (
    <div className="max-w-3xl">
      <header className="mb-4 lg:mb-6">
        <h2 className="text-lg lg:text-xl font-bold mb-1">Persona Style</h2>
        <p className="text-xs lg:text-sm text-slate-400">Adjust communication style and tone</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Sliders Column */}
        <div className="space-y-6">
          {Object.entries(sliders).map(([key, value]) => (
            <div key={key}>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-slate-300 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </label>
                <span className="text-xs text-slate-500">{value}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={value}
                onChange={(e) => handleSliderChange(key, parseInt(e.target.value))}
                className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-cyan-400"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>Low</span>
                <span>High</span>
              </div>
            </div>
          ))}
        </div>

        {/* Live Preview Panel */}
        <div className="sticky top-0">
          <div className="border border-slate-800 rounded-xl bg-slate-900/50 p-4">
            <div className="text-xs uppercase tracking-[0.2em] text-cyan-400/80 mb-3">
              🎨 Live Preview
            </div>
            <div className="text-sm text-slate-300 space-y-2">
              <p className="italic text-slate-400">Sample response based on current settings:</p>
              <p className="bg-cyan-400/10 border border-cyan-400/20 rounded-lg p-3 text-cyan-50">
                "Hello! I'd be happy to help with that. Let me share my thoughts on this topic..."
              </p>
              <p className="text-xs text-slate-500">
                Adjusts in real-time as you move the sliders
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button className="px-4 py-2 bg-cyan-400 text-slate-900 font-medium rounded-lg hover:bg-cyan-300 transition">
          Save Persona
        </button>
        <button className="px-4 py-2 border border-slate-700 text-slate-300 rounded-lg hover:bg-slate-800 transition">
          Test in Chat
        </button>
      </div>
    </div>
  );
}

// Tab 3: Values & Boundaries
function ValuesTab({ presence }) {
  const [values, setValues] = useState(['Empathy', 'Honesty', 'Justice']);
  const [topicsToAvoid, setTopicsToAvoid] = useState(['Politics', 'Finance', 'Medical']);
  const [rules, setRules] = useState([
    'Never be rude',
    'Never give medical advice',
    'Never make financial decisions'
  ]);
  const [refusalTemplate, setRefusalTemplate] = useState('educational');
  const [newValue, setNewValue] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [newRule, setNewRule] = useState('');

  const addValue = () => {
    if (newValue.trim()) {
      setValues([...values, newValue.trim()]);
      setNewValue('');
    }
  };

  const addTopic = () => {
    if (newTopic.trim()) {
      setTopicsToAvoid([...topicsToAvoid, newTopic.trim()]);
      setNewTopic('');
    }
  };

  const addRule = () => {
    if (newRule.trim()) {
      setRules([...rules, newRule.trim()]);
      setNewRule('');
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h2 className="text-lg lg:text-xl font-bold mb-1">Values & Boundaries</h2>
        <p className="text-xs lg:text-sm text-slate-400">Define core values and topics to avoid</p>
      </header>

      {/* Core Values */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-3">Core Values</label>
        <div className="flex flex-wrap gap-2 mb-3">
          {values.map((value, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-400/10 border border-cyan-400/40 text-cyan-200 rounded-full text-sm"
            >
              {value}
              <button
                onClick={() => setValues(values.filter((_, i) => i !== idx))}
                className="text-cyan-400/60 hover:text-cyan-300"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addValue()}
            placeholder="Add a value..."
            className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 text-sm focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400"
          />
          <button
            onClick={addValue}
            className="px-4 py-2 bg-cyan-400/20 border border-cyan-400/40 text-cyan-300 rounded-lg hover:bg-cyan-400/30 transition text-sm font-medium"
          >
            + Add
          </button>
        </div>
      </div>

      {/* Topics to Avoid */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-3">Topics to Avoid</label>
        <div className="flex flex-wrap gap-2 mb-3">
          {topicsToAvoid.map((topic, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-2 px-3 py-1 bg-amber-400/10 border border-amber-400/40 text-amber-200 rounded-full text-sm"
            >
              {topic}
              <button
                onClick={() => setTopicsToAvoid(topicsToAvoid.filter((_, i) => i !== idx))}
                className="text-amber-400/60 hover:text-amber-300"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newTopic}
            onChange={(e) => setNewTopic(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addTopic()}
            placeholder="Add a topic..."
            className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 text-sm focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400"
          />
          <button
            onClick={addTopic}
            className="px-4 py-2 bg-amber-400/20 border border-amber-400/40 text-amber-300 rounded-lg hover:bg-amber-400/30 transition text-sm font-medium"
          >
            + Add
          </button>
        </div>
      </div>

      {/* Never-Do Rules */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-3">Never-Do Rules</label>
        <ul className="space-y-2 mb-3">
          {rules.map((rule, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
              <span className="text-red-400 mt-0.5">•</span>
              <span className="flex-1">{rule}</span>
              <button
                onClick={() => setRules(rules.filter((_, i) => i !== idx))}
                className="text-slate-500 hover:text-red-400 transition"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <input
            type="text"
            value={newRule}
            onChange={(e) => setNewRule(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addRule()}
            placeholder="Add a rule..."
            className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 text-sm focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400"
          />
          <button
            onClick={addRule}
            className="px-4 py-2 bg-red-400/20 border border-red-400/40 text-red-300 rounded-lg hover:bg-red-400/30 transition text-sm font-medium"
          >
            + Add Rule
          </button>
        </div>
      </div>

      {/* Refusal Template */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-3">Refusal Template</label>
        <div className="flex gap-3">
          {['polite', 'firm', 'educational'].map((template) => (
            <label key={template} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="refusalTemplate"
                value={template}
                checked={refusalTemplate === template}
                onChange={(e) => setRefusalTemplate(e.target.value)}
                className="text-cyan-400"
              />
              <span className="text-sm text-slate-300 capitalize">{template}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="pt-4 border-t border-slate-800">
        <button className="px-4 py-2 bg-cyan-400 text-slate-900 font-medium rounded-lg hover:bg-cyan-300 transition">
          Save Values & Rules
        </button>
      </div>
    </div>
  );
}

// Tab 4: Safety & Governance
function SafetyTab() {
  const [categories, setCategories] = useState([
    { id: 'financial', label: 'Financial Advice', enabled: true, risk: 'High' },
    { id: 'medical', label: 'Medical Guidance', enabled: true, risk: 'High' },
    { id: 'identity', label: 'Identity Impersonation', enabled: true, risk: 'Critical' },
    { id: 'hate', label: 'Hate Speech', enabled: true, risk: 'Critical' },
    { id: 'political', label: 'Political Opinion', enabled: false, risk: 'Medium' },
    { id: 'personal', label: 'Personal Data Sharing', enabled: false, risk: 'Medium' },
    { id: 'relationship', label: 'Relationship Advice', enabled: false, risk: 'Low' },
  ]);
  const [highRiskMode, setHighRiskMode] = useState(true);
  const [violationAction, setViolationAction] = useState('block');

  const toggleCategory = (id) => {
    setCategories(categories.map(cat => 
      cat.id === id ? { ...cat, enabled: !cat.enabled } : cat
    ));
  };

  const getRiskColor = (risk) => {
    switch(risk) {
      case 'Critical': return 'text-red-400 bg-red-400/10 border-red-400/40';
      case 'High': return 'text-orange-400 bg-orange-400/10 border-orange-400/40';
      case 'Medium': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/40';
      case 'Low': return 'text-green-400 bg-green-400/10 border-green-400/40';
      default: return 'text-slate-400 bg-slate-400/10 border-slate-400/40';
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h2 className="text-lg lg:text-xl font-bold mb-1">Safety & Governance</h2>
        <p className="text-xs lg:text-sm text-slate-400">Configure governance rules and risk levels</p>
      </header>

      {/* Governance Categories */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-3">Governance Categories (7)</label>
        <div className="space-y-2">
          {categories.map((cat) => (
            <div
              key={cat.id}
              className="flex items-center justify-between p-3 border border-slate-800 rounded-lg hover:border-slate-700 transition"
            >
              <label className="flex items-center gap-3 cursor-pointer flex-1">
                <input
                  type="checkbox"
                  checked={cat.enabled}
                  onChange={() => toggleCategory(cat.id)}
                  className="w-4 h-4 text-cyan-400 rounded"
                />
                <span className="text-sm text-slate-300">{cat.label}</span>
              </label>
              <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${getRiskColor(cat.risk)}`}>
                {cat.risk} Risk
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* High-Risk Mode */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-3">High-Risk Mode</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="highRiskMode"
              checked={highRiskMode}
              onChange={() => setHighRiskMode(true)}
              className="text-cyan-400"
            />
            <span className="text-sm text-slate-300">Enabled</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="highRiskMode"
              checked={!highRiskMode}
              onChange={() => setHighRiskMode(false)}
              className="text-cyan-400"
            />
            <span className="text-sm text-slate-300">Disabled</span>
          </label>
        </div>
        {highRiskMode && (
          <p className="text-xs text-slate-500 mt-2">
            ⚠️ Strict filtering enabled for high-risk categories
          </p>
        )}
      </div>

      {/* Action on Violation */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-3">Action on Violation</label>
        <div className="flex gap-4">
          {['block', 'warn', 'allow'].map((action) => (
            <label key={action} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="violationAction"
                value={action}
                checked={violationAction === action}
                onChange={(e) => setViolationAction(e.target.value)}
                className="text-cyan-400"
              />
              <span className="text-sm text-slate-300 capitalize">{action} {action === 'block' && '& Log'}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="pt-4 border-t border-slate-800">
        <button className="px-4 py-2 bg-cyan-400 text-slate-900 font-medium rounded-lg hover:bg-cyan-300 transition">
          Save Governance Rules
        </button>
      </div>
    </div>
  );
}

// Tab 5: Access Control
function AccessTab() {
  const [sharedUsers, setSharedUsers] = useState([
    {
      id: 1,
      email: 'john@example.com',
      role: 'Family',
      permissions: ['chat', 'view'],
      addedAt: '2024-11-10'
    }
  ]);
  const [pendingRequests, setPendingRequests] = useState([
    { id: 1, email: 'alice@example.com', role: 'Viewer' },
    { id: 2, email: 'bob@example.com', role: 'Contributor' }
  ]);
  const [showShareModal, setShowShareModal] = useState(false);

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h2 className="text-lg lg:text-xl font-bold mb-1">Access Control</h2>
        <p className="text-xs lg:text-sm text-slate-400">Manage who can access this presence</p>
      </header>

      {/* Shared With */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-slate-300">Shared With</label>
          <button
            onClick={() => setShowShareModal(true)}
            className="px-3 py-1.5 bg-cyan-400/20 border border-cyan-400/40 text-cyan-300 rounded-lg hover:bg-cyan-400/30 transition text-sm font-medium"
          >
            + Share Access
          </button>
        </div>

        <div className="space-y-3">
          {sharedUsers.length === 0 ? (
            <div className="border border-slate-800 rounded-xl bg-slate-900/50 p-6 text-center text-slate-400 text-sm">
              No users have access yet
            </div>
          ) : (
            sharedUsers.map((user) => (
              <div
                key={user.id}
                className="border border-slate-800 rounded-xl bg-slate-900/50 p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-sm font-semibold text-slate-300">
                      {user.email[0].toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-200">{user.email}</div>
                      <div className="text-xs text-slate-500">Role: {user.role}</div>
                      <div className="text-xs text-slate-500">
                        Permissions: {user.permissions.join(', ')}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="px-2 py-1 text-xs border border-slate-700 text-slate-400 rounded hover:bg-slate-800 transition">
                      Edit
                    </button>
                    <button className="px-2 py-1 text-xs border border-red-400/40 text-red-400 rounded hover:bg-red-400/10 transition">
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Pending Requests */}
      {pendingRequests.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-3">
            Pending Requests ({pendingRequests.length})
          </label>
          <ul className="space-y-2">
            {pendingRequests.map((req) => (
              <li
                key={req.id}
                className="flex items-center justify-between p-3 border border-amber-400/20 bg-amber-400/5 rounded-lg text-sm"
              >
                <span className="text-slate-300">
                  {req.email} - {req.role}
                </span>
                <div className="flex gap-2">
                  <button className="px-2 py-1 text-xs bg-cyan-400/20 border border-cyan-400/40 text-cyan-300 rounded hover:bg-cyan-400/30">
                    Approve
                  </button>
                  <button className="px-2 py-1 text-xs border border-slate-700 text-slate-400 rounded hover:bg-slate-800">
                    Deny
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 w-96 max-w-[90vw]">
            <h3 className="text-lg font-bold mb-4">Share Access to Presence</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-300 mb-2">Email</label>
                <input
                  type="email"
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-2">Role</label>
                <select className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-sm">
                  <option>Viewer - View only</option>
                  <option>Contributor - Edit memory & chat</option>
                  <option>Family - Full access</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowShareModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-700 text-slate-300 rounded-lg hover:bg-slate-800 transition"
                >
                  Cancel
                </button>
                <button className="flex-1 px-4 py-2 bg-cyan-400 text-slate-900 font-medium rounded-lg hover:bg-cyan-300 transition">
                  Send Invitation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
